import { orderBy } from "lodash";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from "../../internal";
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const VOCABULARY_GROUP_ENTITY_TOKEN = new StateToken<VocabularyGroupEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.VOCABULARY_GROUP_ENTITY);

export type VocabularyGroupEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.VocabularyGroupEntityModels.VocabularyGroup>;

@State<VocabularyGroupEntityStateModel>({
  name: VOCABULARY_GROUP_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class VocabularyGroupEntityState extends HistoryEntityBaseState<FlatModels.VocabularyGroupEntityModels.VocabularyGroup, VocabularyGroupEntityStateModel> {
  //<editor-fold desc="Selectors">
	
  @Selector([VocabularyGroupEntityState])
  static override typedEntities$(state: VocabularyGroupEntityStateModel): { [id: string]: FlatModels.VocabularyGroupEntityModels.VocabularyGroup } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
	
  @Selector([VocabularyGroupEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.VocabularyGroupEntityModels.VocabularyGroup }): FlatModels.VocabularyGroupEntityModels.VocabularyGroup[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
	
  @Selector([VocabularyGroupEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.VocabularyGroupEntityModels.VocabularyGroup }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
	
  @Selector([VocabularyGroupEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.VocabularyGroupEntityModels.VocabularyGroup }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([
    VocabularyGroupEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedPage$,
  ])
  static currentSelectedVocabularyGroups$(typedEntities: { [id: string]: FlatModels.VocabularyGroupEntityModels.VocabularyGroup },
                                          currentSelectedPage: FlatModels.PageEntityModels.PageEntityType) {
    if (currentSelectedPage && currentSelectedPage.type === FlatModels.PageEntityModels.PageType.enum.Vocabulary) {
      const currentSelectedVocabulary = currentSelectedPage as FlatModels.PageEntityModels.Vocabulary;
      return orderBy(currentSelectedVocabulary.vocabularyGroups.reduce((arr, group) => {
        const entity = typedEntities[group];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.VocabularyGroupEntityModels.VocabularyGroup[]), (a) => a.order || 0, 'asc');
    }
    return [];
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
	
  @Action(FlatActions.VocabularyGroupEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<VocabularyGroupEntityStateModel>,
                            action: FlatActions.VocabularyGroupEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<VocabularyGroupEntityStateModel>,
                              action: FlatActions.VocabularyGroupEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<VocabularyGroupEntityStateModel>,
                               action: FlatActions.VocabularyGroupEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<VocabularyGroupEntityStateModel>,
                                 action: FlatActions.VocabularyGroupEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.VocabularyGroupEntityActions.AddEntity)
  @Undoable(
    FlatActions.VocabularyGroupEntityActions.AddEntity,
    VOCABULARY_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<VocabularyGroupEntityStateModel>,
                     action: FlatActions.VocabularyGroupEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.VocabularyGroupEntityActions.UpdateEntity,
    VOCABULARY_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<VocabularyGroupEntityStateModel>,
                        action: FlatActions.VocabularyGroupEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.VocabularyGroupEntityActions.RemoveEntity,
    VOCABULARY_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<VocabularyGroupEntityStateModel>,
                        action: FlatActions.VocabularyGroupEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">
	
  @Action(FlatActions.VocabularyGroupEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<VocabularyGroupEntityStateModel>,
                        action: FlatActions.VocabularyGroupEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<VocabularyGroupEntityStateModel>,
                        action: FlatActions.VocabularyGroupEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
	
  @Action(FlatActions.VocabularyGroupEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<VocabularyGroupEntityStateModel>,
                            action: FlatActions.VocabularyGroupEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>
	
  @Action(FlatActions.VocabularyGroupEntityActions.AddVocabularyWord)
  addVocabularyWord({getState, patchState}: StateContext<VocabularyGroupEntityStateModel>,
                    {id, vocabularyWord}: FlatActions.VocabularyGroupEntityActions.AddVocabularyWord) {
    // Update Entity
    const entities = structuredClone(getState().entities);
    const entity = entities[id];

    if (Array.isArray(entity.vocabularyWords)) {
      entity.vocabularyWords.push(vocabularyWord.id);
    } else {
      entity.vocabularyWords = [vocabularyWord.id];
    }

    // Patch State
    patchState({
      entities
    });
  }

  @Action(FlatActions.VocabularyGroupEntityActions.RemoveVocabularyWord)
  removeVocabularyWord({getState, patchState}: StateContext<VocabularyGroupEntityStateModel>,
                       {id, vocabularyWord}: FlatActions.VocabularyGroupEntityActions.RemoveVocabularyWord) {
    // Update Entity
    const entities = structuredClone(getState().entities);
    const entity = entities[id];

    if (Array.isArray(entity.vocabularyWords)) {
      entity.vocabularyWords.filter(vw => vw !== vocabularyWord.id);
    }

    // Patch State
    patchState({
      entities
    });
  }

  //</editor-fold>
}
